/* eslint-disable @typescript-eslint/no-explicit-any */
import { action, computed, observable } from "mobx";

import { ApiUrls, PrincipalConfig } from "@app/AppConstants";
import { PromiseCompletion } from "@app/Classes/PromiseCompletion";
import { AircraftFamilyDto, AirportDto, AirportsListFilter, CarrierDto, CarrierPantryCodeDto, PantryCompletenessDto, PantryGapDto, StationPantryCodeDto, WaterQuantityCompletenessDto, WaterQuantityGapDto } from "@app/Models/WebApiModels";
import ApiService from "@app/Services/ApiService";
import { MatchComparer, stringSearchService } from "@app/Services/StringSearchService";
import { configurationStore } from "@app/Stores/ConfigurationStore";
import { AzureAuth } from "@app/Components/AzureAuth";
import { LocalStorageGet, LocalStorageSet } from "@app/Helpers/LocalStorageHelper";

const SELECTED_STATION_STORAGE_KEY = 'SelectedStationFourLetterCode';

export type AirportDtoExt = AirportDto & {
    pantries?: StationPantryCodeDto[];
    [key: string]: any;
};

enum SearchMatchRank {
    ExactCode = 0,
    Includes = 1,
    None = 2
}

export class StationsStore {
    @observable carriers: CarrierDto[] = [];
    @observable families: AircraftFamilyDto[] = [];
    @observable airports: AirportDto[] = [];
    @observable carriersPantryCodes: CarrierPantryCodeDto[] = [];
    @observable stationsPantryCodes: StationPantryCodeDto[] = [];

    @observable filter: AirportsListFilter | null = null;
    @observable selectedStation: AirportDtoExt | null = null;
    @observable searchString: string = '';
    @observable showOnlyStationsMissingPantry: boolean = false;
    @observable showOnlyStationsMissingWaterQuantity: boolean = false;

    /** Forces tab remount via React key after a bulk import replaces the data tabs hold locally. */
    @observable importRevision: number = 0;

    @observable stationsMissingPantryCount: number = 0;
    @observable pantryGaps: PantryGapDto[] = [];
    @observable stationsMissingWaterQuantityCount: number = 0;
    @observable waterQuantityGaps: WaterQuantityGapDto[] = [];

    public loader: PromiseCompletion = new PromiseCompletion();

    public async initFilter() {
        const configValue: string | null = await configurationStore.get(PrincipalConfig.StationsListGroup, PrincipalConfig.ReportFiltersKey, this.loader);
        const filter: AirportsListFilter | null = configValue ? ApiService.typeCheck(JSON.parse(configValue)) : null;
        this.filter = { includeAll: filter?.includeAll || false };
    }

    @action.bound
    public async onFilterChanged(filter: AirportsListFilter) {
        this.filter = filter;
        configurationStore.set(PrincipalConfig.StationsListGroup, PrincipalConfig.ReportFiltersKey, JSON.stringify(filter));

        await Promise.all([
            this.loadAirports(),
            this.loadPantryData()
        ]);
    }

    @action
    selectStation(fourLetterCode: string | null) {
        this.selectedStation = this.stations.find(s => s.fourLetterCode === fourLetterCode) || null;
        if (fourLetterCode) {
            LocalStorageSet(SELECTED_STATION_STORAGE_KEY, fourLetterCode);
        }
    }

    @action
    restoreLastSelectedStation() {
        if (this.selectedStation) return;

        const savedFourLetterCode = LocalStorageGet<string>(SELECTED_STATION_STORAGE_KEY);
        if (savedFourLetterCode) {
            this.selectStation(savedFourLetterCode);
        }
    }

    @action.bound
    setShowOnlyStationsMissingPantry(value: boolean) {
        this.showOnlyStationsMissingPantry = value;
    }

    @action.bound
    setShowOnlyStationsMissingWaterQuantity(value: boolean) {
        this.showOnlyStationsMissingWaterQuantity = value;
    }

    @action.bound
    bumpImportRevision() {
        this.importRevision++;
    }

    @action.bound
    onSearchTermChanged(_?: React.ChangeEvent<HTMLInputElement>, newValue?: string) {
        this.searchString = newValue || '';
    }

    @computed
    get textComparer() {
        const comparator: MatchComparer = (v, t, s) => {
            return stringSearchService.prepareString(v)?.indexOf(stringSearchService.prepareString(t) ?? '', s) ?? -1;
        };
        return comparator;
    }

    @computed
    get searchTerms() {
        const searchStr = this.searchString;
        return searchStr.split(/[ ,]+/).filter((x) => !!x);
    }

    public async loadData() {
        await Promise.all([
            this.loadStaticData(),
            this.loadAirports(),
            this.loadPantryData(),
            this.loadStationsDataCompleteness()
        ]);
    }

    @action
    public async loadStaticData() {
        const [carriersRes, familiesRes, codesRes] = await Promise.all([
            ApiService.get<CarrierDto[]>(ApiUrls.CarriersUrl, null, { completion: this.loader }),
            ApiService.get<AircraftFamilyDto[]>(ApiUrls.AircraftFamiliesUrl, null, { completion: this.loader }),
            AzureAuth.canSeeStationPantry
                ? ApiService.get<CarrierPantryCodeDto[]>(ApiUrls.PantryCarrierCodesUrl, null, { completion: this.loader })
                : Promise.resolve(null),
        ]);
        this.carriers = carriersRes.data.sort((a, b) => a.sortOrder - b.sortOrder);
        this.families = familiesRes.data;
        this.carriersPantryCodes = codesRes?.data ?? [];
    }

    @action
    public async loadAirports() {
        await this.initFilter();

        const param = {
            includeAll: this.filter ? this.filter.includeAll : false
        };
        const { data } = await ApiService.get<AirportDto[]>(ApiUrls.AirportUrl, param, { completion: this.loader });
        this.airports = data;
    }

    @action.bound
    public async loadPantryData() {
        if (!AzureAuth.canSeeStationPantry) {
            this.stationsPantryCodes = [];
            return;
        }

        const { data } = await ApiService.get<StationPantryCodeDto[]>(ApiUrls.PantryStationCodesUrl, null, { completion: this.loader });
        this.stationsPantryCodes = data;
    }

    @computed
    public get stations() {
        return this.airports.map(station => {
            const pantries = this.stationsPantryCodes.filter(p => p.airportFourLetterCode === station.fourLetterCode);

            // for stations dx-grid
            const dynamicFields: Record<string, string> = {};
            pantries.forEach(pantry => {
                const pantryCode = this.carriersPantryCodes.find(c => c.id === pantry.carrierPantryCodeId)?.pantryCode;
                if (pantryCode) {
                    dynamicFields[`${pantry.carrierCode}_${pantry.aircraftFamilyId}`] = pantryCode;
                }
            });

            const pantryGaps: Record<string, string> = {};
            this.getStationPantryGaps(station.fourLetterCode).forEach(gap => {
                pantryGaps[`${gap.carrierCode}_${gap.aircraftFamilyId}`] = gap.closestFlightLabel ?? '';
            });

            const waterQuantityGapHint = this.getStationWaterQuantityGaps(station.fourLetterCode)
                .map(gap => `${gap.originThreeLetterCode} - ${gap.destinationThreeLetterCode} / ${gap.aircraftTypeGroupName} (${gap.closestFlightLabel})`)
                .join('\n');

            return {
                ...station,
                pantries: pantries.length ? pantries : undefined,
                pantryGaps,
                waterQuantityGapHint,
                ...dynamicFields
            };
        });
    }

    @computed
    public get filteredStations() {
        let filteredList = [...this.stations];

        if (this.showOnlyStationsMissingPantry) {
            filteredList = filteredList.filter(s => this.isStationMissingPantry(s.fourLetterCode));
        }

        if (this.showOnlyStationsMissingWaterQuantity) {
            filteredList = filteredList.filter(s => this.isStationMissingWaterQuantity(s.fourLetterCode));
        }

        const searchStringParts = this.searchTerms;

        if (searchStringParts.length) {
            const preparedSearchTerms = searchStringParts.map(term => stringSearchService.prepareString(term));

            filteredList = filteredList
                .map(station => ({ station, rank: this.getSearchMatchRank(station, searchStringParts, preparedSearchTerms) }))
                .filter(x => x.rank !== SearchMatchRank.None)
                .sort((a, b) => a.rank - b.rank)
                .map(x => x.station);
        }

        return filteredList;
    }

    private getSearchMatchRank(station: AirportDtoExt, searchTerms: string[], preparedSearchTerms: (string | undefined)[]): SearchMatchRank {
        const threeLetterCode = stringSearchService.prepareString(station.threeLetterCode);
        const fourLetterCode = stringSearchService.prepareString(station.fourLetterCode);

        if (preparedSearchTerms.some(term => term === threeLetterCode || term === fourLetterCode)) {
            return SearchMatchRank.ExactCode;
        }

        const comparator = this.textComparer;
        const isIncluded = searchTerms.some(term =>
            comparator(station.threeLetterCode, term, 0) !== -1
            || comparator(station.fourLetterCode, term, 0) !== -1
            || comparator(station.name, term, 0) !== -1);

        return isIncluded ? SearchMatchRank.Includes : SearchMatchRank.None;
    }

    async loadStationsDataCompleteness() {
        await Promise.all([
            this.loadStationsPantryStatus(),
            this.loadStationsWaterQuantityStatus()
        ]);
    }

    @action
    async loadStationsPantryStatus() {
        if (!AzureAuth.canWriteStationPantry) {
            this.stationsMissingPantryCount = 0;
            this.pantryGaps = [];
            return;
        }
        const { data } = await ApiService.get<PantryCompletenessDto>(
            ApiUrls.PantryFutureDestinationStatusUrl, undefined, { showLoader: true });
        this.stationsMissingPantryCount = data.stationsAffectedCount;
        this.pantryGaps = data.gaps ?? [];
    }

    @action
    async loadStationsWaterQuantityStatus() {
        if (!AzureAuth.canWriteWaterQuantity) {
            this.stationsMissingWaterQuantityCount = 0;
            this.waterQuantityGaps = [];
            return;
        }
        const { data } = await ApiService.get<WaterQuantityCompletenessDto>(
            ApiUrls.WaterQuantityFutureFlightStatusUrl, undefined, { showLoader: true });
        this.stationsMissingWaterQuantityCount = data.stationsAffectedCount;
        this.waterQuantityGaps = data.gaps ?? [];
    }

    @computed
    get stationsMissingDataCount(): number {
        const affected = new Set<string>();
        this.pantryGaps.forEach(g => g.stationFourLetterCode && affected.add(g.stationFourLetterCode));
        this.waterQuantityGaps.forEach(g => g.stationFourLetterCode && affected.add(g.stationFourLetterCode));
        return affected.size;
    }

    isStationMissingPantry(fourLetterCode?: string) {
        return !!fourLetterCode && this.pantryGaps.some(g => g.stationFourLetterCode === fourLetterCode);
    }

    getStationPantryGaps(fourLetterCode?: string): PantryGapDto[] {
        return fourLetterCode ? this.pantryGaps.filter(g => g.stationFourLetterCode === fourLetterCode) : [];
    }

    getPantryGap(fourLetterCode: string | undefined, carrierCode: string, aircraftFamilyId: string): PantryGapDto | undefined {
        return this.pantryGaps.find(g =>
            g.stationFourLetterCode === fourLetterCode
            && g.carrierCode === carrierCode
            && g.aircraftFamilyId === aircraftFamilyId);
    }

    isStationMissingWaterQuantity(fourLetterCode?: string) {
        return !!fourLetterCode && this.waterQuantityGaps.some(g => g.stationFourLetterCode === fourLetterCode);
    }

    getStationWaterQuantityGaps(fourLetterCode?: string): WaterQuantityGapDto[] {
        return fourLetterCode ? this.waterQuantityGaps.filter(g => g.stationFourLetterCode === fourLetterCode) : [];
    }

    getWaterQuantityGap(fourLetterCode: string | undefined, origin: string, destination: string, aircraftTypeGroupId: string): WaterQuantityGapDto | undefined {
        return this.waterQuantityGaps.find(g =>
            g.stationFourLetterCode === fourLetterCode
            && g.originFourLetterCode === origin
            && g.destinationFourLetterCode === destination
            && g.aircraftTypeGroupId === aircraftTypeGroupId);
    }
}

export const stationsStore = new StationsStore();